import lodash from 'lodash'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import AddressPicker, {
  TAddressPicker,
} from '../../../1stlog-ui/src/components/AddressPicker'
import Button from '../../../1stlog-ui/src/components/Button'
import Checkbox from '../../../1stlog-ui/src/components/Checkbox'
import Field, {
  useField,
  useFieldContext,
} from '../../../1stlog-ui/src/components/Field'
import { useForm } from '../../../1stlog-ui/src/components/Form'
import LoaderOverlay from '../../../1stlog-ui/src/components/LoaderOverlay'
import { IOrderEntryAddress } from '../OrderEntry/types'
import AddressBookControls from './AddressBook/AddressBookControls'
import AddressBookDialog, {
  useAddressBookDialog,
} from './AddressBook/AddressBookDialog'
import useAddressBook from './AddressBook/useAddressBook'
import AddressSuggestions from './AddressSuggestions'
import Contact from './Contact'
import ContactExpander from './ContactExpander'
import DriverInstructions from './DriverInstructions'
import NameSearch from './NameSearch'
// import AddressBook from "./AddressBook"

const Wrapper = styled.div`
  position: relative;
`

interface TAddressPickerAdvancedProps extends TAddressPicker {
  requireEmail?: boolean
  showDriverInstructions?: boolean
  isEoriRequired?: boolean
  withoutAddressbook?: boolean
  hideContact?: boolean
  hideCustomerAddressHint?: boolean
  hideReference?: boolean
  hidePrenameSurname?: boolean
  isPhoneRequired?: boolean
  isNameRequired?: boolean
  showEori?: boolean
}

/**
 * Only used in OrderEntry.
 */
const AddressPickerAdvanced = (
  props: TAddressPickerAdvancedProps,
): ReactElement => {
  const {
    requireEmail,
    showDriverInstructions,
    isEoriRequired,
    withoutAddressbook,
    hideContact,
    hideCustomerAddressHint,
    hideReference,
    hidePrenameSurname,
    isPhoneRequired,
    isNameRequired,
    showEori,
    ...rest
  } = props

  // save address to address book
  // load from address book
  // update in address book
  const { open: openAddressBook } = useAddressBookDialog()

  const { isLoading } = useAddressBook()

  const onApply = useCallback((values) => {
    global.addressPickerSkipZipCityClear = 1
    setAppliedAddress(values)
    setAddress(values)
  }, [])

  const customComponents = useMemo(
    () => ({
      // TODO: add addressbook search
      name1: (
        <NameSearch
          onOpenAddressBook={openAddressBook}
          withoutAddressbook={withoutAddressbook}
          onApply={onApply}
        />
      ),
    }),
    [onApply],
  )

  const { prefix = '' } = useFieldContext()

  const showContact = useField('showContact')

  const form = useForm()

  const [address, setAddress] = useField(prefix, {
    asState: true,
    ignorePrefix: true,
  })
  const [appliedAddress, setAppliedAddress] = useState<IOrderEntryAddress>()

  const onClear = (): void => {
    const values = lodash.get(form.getState(), `values.${prefix}`)
    form.change(prefix, { country: lodash.get(values, 'country') })
  }

  const customTooltips = {
    // name1: i18n.t('note_company_name', 'Bitte notieren Sie hier den Firmennamen'),
    // name2: i18n.t(
    //   'note_contact_person',
    //   'Falls vorhanden notieren Sie hier bitte die Ansprechperson',
    // ),
    // name3: i18n.t(
    //   'note_additional_texts',
    //   'Hier können Sie Zusatztexte für Namen oder Adressen notieren wie `Abteilung 123`, `Eingang West`, `Casa Rumantsch`, usw. Bitte schreiben Sie keine Anweisungen in dieses Feld - diese werden nicht berücksichtigt.',
    // ),
    // street: i18n.t(
    //   'note_street_name',
    //   'Bitte notieren Sie hier die genau Strassenbezeichnung. Bitte nehmen Sie zu Kenntnis, dass wir keine Postfächer beliefern.',
    // ),
    // streetNumber: i18n.t(
    //   'note_exact_street_number',
    //   'Falls vorhanden: bitte tragen Sie hier die genaue Strassennummer ein.',
    // ),
    // streetNumberAddition: i18n.t(
    //   'note_street_number_addition',
    //   'Hier können Sie Adresszusätze wie `Sektor A`, `Rampe 5`, `Halle 3`, `Eingang 2A`, usw. ergänzen. Bitte schreiben Sie keine Anweisungen in dieses Feld - diese werden nicht berücksichtigt.',
    // ),
  }

  return (
    <Wrapper>
      <LoaderOverlay isLoading={isLoading} offset={-12} />
      {!withoutAddressbook && <AddressBookDialog onApply={onApply} />}
      {!withoutAddressbook && (
        <AddressSuggestions
          currentAddress={address}
          onApply={onApply}
          appliedAddress={appliedAddress}
        />
      )}
      <AddressPicker
        {...rest}
        dontNest
        customTooltips={customTooltips}
        showTooltips
        showReference={!hideReference}
        customComponents={customComponents}
        largerCountry
      />
      <DriverInstructions />
      {!hideContact && (
        <>
          <div className="my-2">
            <Field name="showContact">
              <Checkbox>
                {i18n.t('showContact', 'Kontaktdaten einblenden')}
              </Checkbox>
            </Field>
          </div>
          <ContactExpander
            alwaysOpen={
              requireEmail ||
              isEoriRequired ||
              isPhoneRequired ||
              isNameRequired
            }
          />
          <Contact
            requireEmail={requireEmail}
            isEoriRequired={isEoriRequired}
            hidden={!showContact}
            hideCustomerAddressHint={hideCustomerAddressHint}
            hidePrenameSurname={hidePrenameSurname}
            isPhoneRequired={isPhoneRequired}
            showEori={showEori}
            isNameRequired={isNameRequired}
          />
        </>
      )}
      {!withoutAddressbook && (
        <div>
          <div className="d-flex justify-content-between">
            <AddressBookControls onApply={onApply} />
            <div>
              <div>
                <Button
                  sm
                  icon="fas fa-eraser"
                  tooltip={i18n.t(`clear_addressfields`, `Adressfelder leeren`)}
                  onClick={onClear}
                >
                  {i18n.t(`clear`, `Leeren`)}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Wrapper>
  )
}

export default AddressPickerAdvanced
